import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Loader2, AlertCircle, CheckCircle2 } from 'lucide-react';
import { apiService } from '@/lib/api-functions/api-service';
import { useToast } from '@/hooks/use-toast';

import { usePlatformTranslation } from '@/hooks/usePlatformTranslation';
interface AffectedItem {
  id: string;
  name: string;
  status?: string;
  folder?: string | null;
  blueprintVersion?: { major: number; minor: number; patch: number };
  affectedFields: Array<{
    type: string;
    fieldId: string;
    fieldLabel: string;
    sectionId?: string;
    currentValue?: any;
    hasValue?: boolean;
  }>;
  suggestedUpdates: Array<{
    action: string;
    fieldId: string;
    fieldLabel: string;
    reason?: string;
  }>;
  requiresManualReview: boolean;
}

interface BulkUpdateDialogProps {
  isOpen: boolean;
  onClose: () => void;
  blueprintId: string;
  version: { major: number; minor: number; patch: number };
  onSuccess?: () => void;
}

export function BulkUpdateDialog({
  isOpen,
  onClose,
  blueprintId,
  version,
  onSuccess
}: BulkUpdateDialogProps) {
  const { t } = usePlatformTranslation();
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [affectedItems, setAffectedItems] = useState<AffectedItem[]>([]);
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());
  const [strategy, setStrategy] = useState<'merge' | 'overwrite'>('merge');
  const [updateResults, setUpdateResults] = useState<any>(null);
  const { toast } = useToast();

  // Load affected items when dialog opens
  useEffect(() => {
    if (isOpen && blueprintId && version) {
      loadAffectedItems();
    }
  }, [isOpen, blueprintId, version]);

  // Reset state when dialog opens
  useEffect(() => {
    if (isOpen) {
      setSelectedItems(new Set());
      setUpdateResults(null);
    }
  }, [isOpen]);

  const loadAffectedItems = async () => {
    setLoading(true);
    try {
      const result = await apiService.fetchBlueprintAffectedItems(
        blueprintId,
        version.major,
        version.minor,
        version.patch
      );

      if (result.success) {
        // Only show items that can be bulk updated (review severity, not breaking)
        const reviewItems = result.data?.affectedItems?.review || [];
        setAffectedItems(reviewItems);

        // Auto-select all non-breaking items
        const autoSelectIds = new Set(reviewItems.map((item: AffectedItem) => item.id));
        setSelectedItems(autoSelectIds);
      } else {
        toast({
          title: 'Error',
          description: result.error || 'Failed to load affected items',
          variant: 'destructive'
        });
      }
    } catch (error) {
      console.error('Error loading affected items:', error);
      toast({
        title: 'Error',
        description: 'Failed to load affected items',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleToggleItem = (itemId: string) => {
    const newSelected = new Set(selectedItems);
    if (newSelected.has(itemId)) {
      newSelected.delete(itemId);
    } else {
      newSelected.add(itemId);
    }
    setSelectedItems(newSelected);
  };

  const handleSelectAll = () => {
    if (selectedItems.size === affectedItems.length) {
      setSelectedItems(new Set());
    } else {
      setSelectedItems(new Set(affectedItems.map(item => item.id)));
    }
  };

  const handleBulkUpdate = async () => {
    if (selectedItems.size === 0) {
      toast({
        title: 'No items selected',
        description: 'Please select at least one item to update',
        variant: 'destructive'
      });
      return;
    }

    setUpdating(true);
    try {
      const result = await apiService.bulkUpdateGlossaryItems(
        blueprintId,
        version,
        Array.from(selectedItems),
        strategy
      );

      if (result.success) {
        setUpdateResults(result.data.results);
        toast({
          title: 'Bulk Update Complete',
          description: result.message || `${result.data.results.counts.successful} items updated successfully`,
          variant: 'default'
        });

        if (onSuccess) {
          onSuccess();
        }
      } else {
        toast({
          title: 'Update Failed',
          description: result.error || 'Failed to update items',
          variant: 'destructive'
        });
      }
    } catch (error) {
      console.error('Error performing bulk update:', error);
      toast({
        title: 'Error',
        description: 'Failed to perform bulk update',
        variant: 'destructive'
      });
    } finally {
      setUpdating(false);
    }
  };

  const renderContent = () => {
    if (loading) {
      return (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
        </div>
      );
    }

    if (updateResults) {
      return renderUpdateResults();
    }

    if (affectedItems.length === 0) {
      return (
        <div className="flex flex-col items-center justify-center py-8 text-center">
          <CheckCircle2 className="h-12 w-12 text-green-500 mb-3" />
          <p className="text-gray-600">{t('platform.blueprint-version.bulkupdatedialog.no_items_need_to_be_updated')}</p>
        </div>
      );
    }

    return (
      <>
        <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
          <p className="text-sm text-blue-800">
            The following items can be updated automatically. Select which items to update.
          </p>
        </div>

        {/* Strategy Selection */}
        <div className="mb-4 space-y-2">
          <label className="text-sm font-medium">{t('platform.blueprint-version.bulkupdatedialog.update_strategy')}</label>
          <div className="flex gap-2">
            <Button
              variant={strategy === 'merge' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setStrategy('merge')}
            >
              Merge (Recommended)
            </Button>
            <Button
              variant={strategy === 'overwrite' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setStrategy('overwrite')}
            >
              Overwrite
            </Button>
          </div>
          <p className="text-xs text-gray-500">
            {strategy === 'merge' 
              ? 'Merge: Add new fields while preserving existing values'
              : 'Overwrite: Replace entire structure with new template'}
          </p>
        </div>

        {/* Select All */}
        <div className="flex items-center gap-2 mb-3 pb-3 border-b">
          <Checkbox
            checked={selectedItems.size === affectedItems.length && affectedItems.length > 0}
            onCheckedChange={handleSelectAll}
            id="select-all"
          />
          <label htmlFor="select-all" className="text-sm font-medium cursor-pointer">
            Select All ({selectedItems.size} of {affectedItems.length} selected)
          </label>
        </div>

        {/* Items List */}
        <div className="h-[400px] overflow-y-auto pr-4">
          <div className="space-y-2">
            {affectedItems.map((item) => (
              <div
                key={item.id}
                className={`p-3 border rounded-md transition-colors ${
                  selectedItems.has(item.id) ? 'bg-blue-50 border-blue-200' : 'bg-white border-gray-200'
                }`}
              >
                <div className="flex items-start gap-3">
                  <Checkbox
                    checked={selectedItems.has(item.id)}
                    onCheckedChange={() => handleToggleItem(item.id)}
                    id={`item-${item.id}`}
                  />
                  <div className="flex-1 min-w-0">
                    <label
                      htmlFor={`item-${item.id}`}
                      className="font-medium text-sm cursor-pointer block mb-1"
                    >
                      {item.name}
                    </label>
                    {item.status && (
                      <Badge variant="outline" className="text-xs mb-2">
                        {item.status}
                      </Badge>
                    )}
                    {item.affectedFields.length > 0 && (
                      <div className="text-xs text-gray-600 space-y-1">
                        <div className="font-medium">{t('platform.blueprint-version.bulkupdatedialog.changes')}</div>
                        <ul className="list-disc list-inside ml-2 space-y-0.5">
                          {item.affectedFields.slice(0, 3).map((field, idx) => (
                            <li key={idx}>
                              {field.type === 'missing_field' && `Add: ${field.fieldLabel}`}
                              {field.type === 'obsolete_field' && `Remove: ${field.fieldLabel}`}
                              {field.type === 'modified_field' && `Update: ${field.fieldLabel}`}
                            </li>
                          ))}
                          {item.affectedFields.length > 3 && (
                            <li className="italic">...and {item.affectedFields.length - 3} more</li>
                          )}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </>
    );
  };

  const renderUpdateResults = () => {
    if (!updateResults) return null;

    const { successful, failed, skipped, counts } = updateResults;

    return (
      <div className="space-y-4">
        <div className="grid grid-cols-3 gap-4">
          <div className="text-center p-3 bg-green-50 border border-green-200 rounded-md">
            <div className="text-2xl font-bold text-green-700">{counts.successful}</div>
            <div className="text-xs text-green-600">{t('platform.blueprint-version.bulkupdatedialog.successful')}</div>
          </div>
          <div className="text-center p-3 bg-red-50 border border-red-200 rounded-md">
            <div className="text-2xl font-bold text-red-700">{counts.failed}</div>
            <div className="text-xs text-red-600">{t('platform.blueprint-version.bulkupdatedialog.failed')}</div>
          </div>
          <div className="text-center p-3 bg-gray-50 border border-gray-200 rounded-md">
            <div className="text-2xl font-bold text-gray-700">{counts.skipped}</div>
            <div className="text-xs text-gray-600">{t('platform.blueprint-version.bulkupdatedialog.skipped')}</div>
          </div>
        </div>

        <div className="h-[300px] overflow-y-auto">
          <div className="space-y-3">
            {successful.length > 0 && (
              <div>
                <h4 className="text-sm font-medium text-green-700 mb-2">✓ Successfully Updated</h4>
                <ul className="text-xs space-y-1 ml-4">
                  {successful.map((item: any, idx: number) => (
                    <li key={idx} className="text-gray-700">{item.itemName}</li>
                  ))}
                </ul>
              </div>
            )}

            {failed.length > 0 && (
              <div>
                <h4 className="text-sm font-medium text-red-700 mb-2">✗ Failed</h4>
                <ul className="text-xs space-y-1 ml-4">
                  {failed.map((item: any, idx: number) => (
                    <li key={idx} className="text-gray-700">{item.error}</li>
                  ))}
                </ul>
              </div>
            )}

            {skipped.length > 0 && (
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-2">− Skipped</h4>
                <ul className="text-xs space-y-1 ml-4">
                  {skipped.map((item: any, idx: number) => (
                    <li key={idx} className="text-gray-600">{item.itemName}: {item.reason}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="text-center">{t('platform.blueprint-version.bulkupdatedialog.bulk_update_glossary_items')}</DialogTitle>
          <DialogDescription className="text-center">
            Update multiple items to version {version.major}.{version.minor}.{version.patch}
          </DialogDescription>
        </DialogHeader>

        {renderContent()}

        <DialogFooter>
          {!updateResults ? (
            <>
              <Button variant="outline" onClick={onClose} disabled={updating}>
                Cancel
              </Button>
              <Button
                onClick={handleBulkUpdate}
                disabled={updating || selectedItems.size === 0 || loading}
              >
                {updating ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Updating...
                  </>
                ) : (
                  `Update ${selectedItems.size} Item${selectedItems.size !== 1 ? 's' : ''}`
                )}
              </Button>
            </>
          ) : (
            <Button onClick={onClose}>{t('platform.common.close')}</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default BulkUpdateDialog;

